import { useState, useEffect, useRef, useCallback } from "react";
// import * as Sentry from "@sentry/react";

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

// Material Dashboard 2 React example components
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";

import { UnsavedChangesProvider } from "contexts/UnsavedChangesProvider";

// Material Dashboard 2 React themes
import theme from "assets/theme";

// Material Dashboard 2 React Dark Mode themes
import themeDark from "assets/theme-dark";


// Material Dashboard 2 React routes
import routes from "routes";

// Images
import brandWhite from "assets/images/logo-ct.png";
import brandDark from "assets/images/logo-ct-dark.png";
import { useDispatch, useSelector } from "react-redux";
import { getUserInfo } from "./redux/slices/authSlice";
import SignIn from "layouts/authentication/sign-in"
import { fetchBrands } from "./redux/slices/brand";
import { fetchInfoEffect, fetchCategories, fetchAllRegions, setAdminsMode } from "./redux/slices/common";
import CustomModal from "common/CommonModal";
import MDSnackbar from "components/MDSnackbar";
import { closeSnackbar } from "./redux/slices/common";
import { PublicRoutes } from "HOC/PublicRoute";
import { PrivateRoute } from "HOC/PrivateRoutes";
import { getData } from "utilities/common";
import { isEmptyObject } from "utilities/common";
import CommonDrawer from "common/CommonDrawer";
import ForgotPassword from "layouts/authentication/forgotPassword";
import ResetPassword from "layouts/authentication/resetPassword";
import ResetPincode from "layouts/authentication/resetPincode";
import PageNotFound from "layouts/401";
import { isLoggedIn } from "utilities/common";
import { onMessage } from "firebase/messaging";
import { messaging } from "firebaseInit";
import { AddHyperdriveNotifications, fetchAllHyperdriveNotificationsCountsData, fetchAllNotificationsCountsData, fetchHyperdriveNotificationCount, fetchNotificationCount, storeAddProductNotifications } from "./redux/slices/notifications";
import NotificationCard from "components/notifications/NotificationCard";
import { hyperfwolfAdminRoutes } from "routes";
import { HYPERWOLF } from "utilities/constants";
import { HYPERDRIVE } from "utilities/constants";
import { hyperfDriveAdminRoutes } from "routes";
import ResetHyperdrivePassword from "layouts/authentication/resetHyerdrivePassword";
import { Helmet } from "react-helmet";
import { capitalizeText } from "utilities/common";
import imagelogo from './assets/images/stilo.svg'
import { stiloRoutes } from "routes";
import { STILO } from "utilities/constants";


import { setData } from "utilities/common";
import NotificationCardHyperdrive from "components/notifications/NotificationCardHyperdrive";
export default function App() {
  const controller = useSelector((state) => state.materialUI);
  const { adminsMode = '' } = useSelector(state => state.common)
  const { isOpen = false, color = '', icon = '', content = '', bgWhite = false, anchorOrigin = { vertical: "top", horizontal: "right" }, persist = false } = useSelector(state => state.common.snackbar);
  const { user = {} } = useSelector((state) => state.auth)
  const dispatch = useDispatch();

  const {
    direction,
    layout,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;

  const { pathname } = useLocation();
  const publicPaths = ['/login', '/forgot-password', '/reset-password', '/reset-hyperdrive-password', '/distribution/reset-pincode', '/401'];
  const isPublicRoute = publicPaths.some((p) => pathname.startsWith(p));
  const adminsModeLocal = getData('adminsMode')
  const isHyperdrive = JSON.parse(adminsModeLocal) === HYPERDRIVE

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);
  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const [mount, setMount] = useState(false)

  useEffect(() => {
    const adminsMode = getData('adminsMode')
    if (JSON.parse(adminsMode) === HYPERWOLF) {
      dispatch(fetchCategories({ skip: 0, limit: 1000, userType: 'admin' }))
      dispatch(fetchInfoEffect())
    }
    if (JSON.parse(adminsMode) === HYPERWOLF || JSON.parse(adminsMode) === HYPERDRIVE) return;
    dispatch(fetchBrands({ skip: 0, limit: 1000, userType: 'admin' }))
    dispatch(fetchInfoEffect())
    dispatch(fetchAllRegions())
    dispatch(fetchCategories({ skip: 0, limit: 1000, userType: 'admin' }))

  }, [])

  // State to track user data and admin mode
  const [localUser, setLocalUser] = useState(() => {
    const userData = getData('login-user-info');
    return userData ? JSON.parse(userData) : null;
  });

  const [screenToLoad, setScreenToLoad] = useState('/');
  const isInitialMount = useRef(true);
  const lastPathname = useRef(pathname);

  // Sync localStorage with component state on mount and when pathname changes
  useEffect(() => {
    // Only sync if pathname actually changed (not on every render)
    if (isInitialMount.current || lastPathname.current !== pathname) {
      isInitialMount.current = false;
      lastPathname.current = pathname;

      const userData = getData('login-user-info');
      const parsedUser = userData ? JSON.parse(userData) : null;
      setLocalUser(prevUser => {
        // Only update if user data actually changed
        if (JSON.stringify(prevUser) !== JSON.stringify(parsedUser)) {
          return parsedUser;
        }
        return prevUser;
      });

      const adminsModeData = getData('adminsMode');
      if (adminsModeData) {
        const parsedMode = JSON.parse(adminsModeData);
        // Only dispatch if the value is different from current Redux state to prevent loops
        if (parsedMode !== adminsMode) {
          dispatch(setAdminsMode(parsedMode));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]); // Only re-run when pathname changes (after login), not when state changes

  // Sync with Redux auth state to handle logout
  useEffect(() => {
    const isAuthenticated = isLoggedIn()?.isAuthenticated;
    const userData = getData('login-user-info');
    const parsedUser = userData ? JSON.parse(userData) : null;

    // If user is not authenticated but we have localUser state, clear it
    if (!isAuthenticated && localUser) {
      setLocalUser(null);
      setScreenToLoad('/login');
    } else if (isAuthenticated && parsedUser && !localUser) {
      // If user is authenticated but localUser state is missing, restore it
      setLocalUser(parsedUser);
    }
  }, [user, localUser]); // user comes from Redux auth state

  // Calculate screenToLoad based on current state - only when user is authenticated
  useEffect(() => {
    const isAuthenticated = isLoggedIn()?.isAuthenticated;
    if (!isAuthenticated || !localUser) {
      setScreenToLoad('/login');
      return;
    }

    const modeFromLS = getData('adminsMode') ? JSON.parse(getData('adminsMode')) : null;
    const selectedAdminMode = adminsMode || modeFromLS;

    const userPermissions = localUser?.permissions || {};

    const getPerms = (mode) => {
      if (mode === 'hemp') return Array.isArray(userPermissions.hempPermissions) ? userPermissions.hempPermissions : [];
      if (mode === 'hyperwolf') return Array.isArray(userPermissions.hwPermissions) ? userPermissions.hwPermissions : [];
      if (mode === 'stilo') return Array.isArray(userPermissions.stiloPermissions) ? userPermissions.stiloPermissions : [];
      if (mode === 'hyperdrive') return Array.isArray(userPermissions.hyperdrivePermissions) ? userPermissions.hyperdrivePermissions : [];

      return [];
    };

    let permissionsArray = getPerms(selectedAdminMode);

    // If current mode is set but has no permissions, auto-fallback to first available
    if ((!permissionsArray || permissionsArray.length === 0)) {
      let fallbackMode = null;
      if (Array.isArray(userPermissions.hwPermissions) && userPermissions.hwPermissions.length > 0) fallbackMode = 'hyperwolf';
      else if (Array.isArray(userPermissions.hempPermissions) && userPermissions.hempPermissions.length > 0) fallbackMode = 'hemp';
      else if (Array.isArray(userPermissions.stiloPermissions) && userPermissions.stiloPermissions.length > 0) fallbackMode = 'stilo';
      else if (Array.isArray(userPermissions.hyperdrivePermissions) && userPermissions.hyperdrivePermissions.length > 0) fallbackMode = 'hyperdrive';

      if (fallbackMode && fallbackMode !== selectedAdminMode) {
        // Persist the new mode and update Redux
        setData('adminsMode', fallbackMode);
        dispatch(setAdminsMode(fallbackMode));
        permissionsArray = getPerms(fallbackMode);
      }
    }

    let calculatedScreenToLoad = '/';
    if (Array.isArray(permissionsArray) && permissionsArray.length > 0) {
      const dashboardScreen = permissionsArray.find(
        s => s.title && String(s.title).toLowerCase().trim() === 'dashboard'
      );
      if (dashboardScreen?.redirectUrl) {
        calculatedScreenToLoad = dashboardScreen.redirectUrl;
      } else {
        const found = permissionsArray.find((p) => p?.redirectUrl);
        if (found && found.redirectUrl) {
          calculatedScreenToLoad = found.redirectUrl;
        }
      }
    }

    setScreenToLoad(prev => prev !== calculatedScreenToLoad ? calculatedScreenToLoad : prev);
  }, [localUser, adminsMode]);
  // -----------------------------------------

  // Get user permissions for route filtering
  const userPermissions = localUser?.permissions || {};

  const getRoutes = (allRoutes) => {
    const adminMode = getData('adminsMode') ? JSON.parse(getData('adminsMode')) : null;

    const permsByMode = (mode) => {
      if (mode === 'hemp') return userPermissions.hempPermissions || [];
      if (mode === 'hyperwolf') return userPermissions.hwPermissions || [];
      if (mode === 'stilo') return userPermissions.stiloPermissions || [];
      if (mode === 'hyperdrive') return userPermissions.hyperdrivePermissions || [];

      return [];
    };

    let permissionsForMode = permsByMode(adminMode);
    if (!permissionsForMode || permissionsForMode.length === 0) {
      // fallback to first available permissions
      if ((userPermissions.hwPermissions || []).length > 0) permissionsForMode = userPermissions.hwPermissions;
      else if ((userPermissions.hempPermissions || []).length > 0) permissionsForMode = userPermissions.hempPermissions;
      else if ((userPermissions.stiloPermissions || []).length > 0) permissionsForMode = userPermissions.stiloPermissions;
      else if ((userPermissions.hyperdrivePermissions || []).length > 0) permissionsForMode = userPermissions.hyperdrivePermissions;

    }

    const permittedTitles = (permissionsForMode || []).map(p => (p?.title || '').toString().toLowerCase().trim());

    const filteredRoutes = (allRoutes || []).filter(route => {
      // Only check route if it has a 'title'
      if (!route.title) return false;

      // compare in lowercase
      const rTitle = String(route.title).toLowerCase().trim();
      return permittedTitles.includes(rTitle);
    });

    // Use filteredRoutes as the basis for rendering the sidebar (replacing old filter logic)
    allRoutes = filteredRoutes;

    return allRoutes
      .map((route) => {
        if (route.collapse) {
          // If the route has a 'collapse' property, it means it has nested routes.
          // Recursively call getRoutes for nested routes.
          return getRoutes(route.collapse);
        }
        if (route.route) {
          // If the route has a 'route' property, it means it's a leaf route.
          // Create a React Route with the specified path and associate it with a PrivateRoute.
          return (
            <Route
              exact
              path={route.route}

              element={
                <PrivateRoute>
                  {route.component}
                </PrivateRoute>
              }
              key={route.key}
            />
          );
        }
        // If neither 'collapse' nor 'route' is present, return null.
        return null;
      });
  }



  const { addProductNotifications = 0 } = useSelector(state => state.notifications)
  // OLD NOTIFIACTION
  // const notificationHandler = (payload) => {

  //   const { data } = payload
  //   const { body, approvalId, title, notificationType } = data || {}
  //   if (notificationType === 'order') {
  //     dispatch(storeAddProductNotifications({ ...data, order: body }))
  //     dispatch(fetchAllHyperdriveNotificationsCountsData({ notificationType: 'order' }))
  //   }
  // }
  const count = useSelector(state => state.notifications.notificationCount);

  const notificationHandler = (payload) => {
    if (isHyperdrive) {

      const { data } = payload
      const { body, approvalId, title, type } = data || {}
      if (type === "outOfService") {
        dispatch(AddHyperdriveNotifications({ ...data, order: body }))
        dispatch(fetchHyperdriveNotificationCount())
      }
    }
    else {

      const { data } = payload
      const { body, approvalId, title, notificationType } = data || {}
      if (notificationType === 'order') {
        dispatch(storeAddProductNotifications({ ...data, order: body }))
        dispatch(fetchAllHyperdriveNotificationsCountsData({ notificationType: 'order' }))
      }
    }
  }
  // useEffect(()=>{
  //   const payload = {
  //     "from": "26002918199",
  //     "messageId": "cabc6ff9-63ea-4ac2-a551-45920395e32e",
  //     "notification": {
  //         "title": "Out Of Service",
  //         "body": "Out Of Service"
  //     },
  //     "data": {
  //         "body": "Out Of Service",
  //         "title": "Out Of Service",
  //         "fleetName": "Kunal",
  //         "type": "outOfService"
  //     }
  // }
  //   notificationHandler(payload)
  // },[])
  // useEffect(() => {
  //   dispatch(fetchNotificationCount({ notificationType: 'order' }))
  //     .unwrap()
  //     .then((res) => {
  //       // setCount(res.count);
  //     })
  //     .catch((error) => {
  //    
  //     });
  // }, [addProductNotifications.length, addProductNotifications[0]?.readStatus]);

  const closeSnackbarMsg = () => {
    dispatch(closeSnackbar());
  }

  const handleSnackbarClose = useCallback((event, reason) => {
    if (persist && (reason === "clickaway" || reason === "escapeKey")) {
      return;
    }
    dispatch(closeSnackbar());
  }, [persist, dispatch]);

  useEffect(() => {
    const userData = JSON.parse(getData('login-user-info'))
    if (userData && !isEmptyObject(userData)) {
      dispatch(getUserInfo(userData?._id))
    }
    if (isHyperdrive) {
      dispatch(fetchHyperdriveNotificationCount())
    }

  }, [])


  useEffect(() => {
    setMount(true)
    return () => {
      setMount(false)
    }
  }, [user])


  useEffect(() => {
    if (!messaging) {
      console.warn('messaging is not initialized');
    }
    onMessage(messaging, (payload) => {
      notificationHandler(payload)
    })
  }, [mount])

  useEffect(() => {
    if (mount) {
      const adminsMode = getData('adminsMode')

      if (adminsMode) {
        dispatch(setAdminsMode(JSON.parse(adminsMode)))

      }
    }
  }, [mount, adminsModeLocal])


  const filterRoutes = (routes, depth = 0) => {

    const adminMode = getData('adminsMode') ? JSON.parse(getData('adminsMode')) : null;

    const permsByMode = (mode) => {
      if (mode === 'hemp') return userPermissions.hempPermissions || [];
      if (mode === 'hyperwolf') return userPermissions.hwPermissions || [];
      if (mode === 'stilo') return userPermissions.stiloPermissions || [];
      if (mode === 'hyperdrive') return userPermissions.hyperdrivePermissions || [];
      return [];
    };

    let permissionsForMode = permsByMode(adminMode);
    if (!permissionsForMode || permissionsForMode.length === 0) {
      if ((userPermissions.hwPermissions || []).length > 0) permissionsForMode = userPermissions.hwPermissions;
      else if ((userPermissions.hempPermissions || []).length > 0) permissionsForMode = userPermissions.hempPermissions;
      else if ((userPermissions.stiloPermissions || []).length > 0) permissionsForMode = userPermissions.stiloPermissions;
      else if ((userPermissions.hyperdrivePermissions || []).length > 0) permissionsForMode = userPermissions.hyperdrivePermissions;

    }

    const permittedTitles = (permissionsForMode || []).map(p => (p?.title || '').toString().toLowerCase().trim());

    const processed = (routes || [])
      .reduce((acc, route) => {
        // Existing special-case filters
        const isStiloModeClass = route?.className === 'stilo-mode';
        const access_1 = process.env.REACT_APP_METRC_ACCESS_1;
        const access_2 = process.env.REACT_APP_METRC_ACCESS_2;
        const allowedEmails = [access_1, access_2];

        const isMETRCForOthers = route?.name === 'METRC' && !allowedEmails.includes(user?.email);
        if (isStiloModeClass || isMETRCForOthers) return acc;

        // Permission-based filter: if a title exists, it must be permitted
        let allowedByPermission = true;
        if (route.title) {
          const rTitle = String(route.title).toLowerCase().trim();
          allowedByPermission = permittedTitles.includes(rTitle);
        }
        if (!allowedByPermission) return acc;

        if (route.collapse && Array.isArray(route.collapse)) {
          const filteredChildren = filterRoutes(route.collapse, depth + 1);
          if (filteredChildren && filteredChildren.length > 0) {
            acc.push({ ...route, collapse: filteredChildren });
          }
          // If no permitted children remain, drop this group
          return acc;
        }

        acc.push(route);
        return acc;
      }, []);

    return processed;
  };

  useEffect(() => {
    if (!mount) return;
    const gmapScriptEl = document.createElement(`script`)
    gmapScriptEl.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_KEY}&libraries=places`
    document.querySelector(`body`).insertAdjacentElement(`beforeend`, gmapScriptEl)
    return () => {
      window.google = null;
    }
  }, [mount])


  return (<>

    {!isLoggedIn()?.isAuthenticated && !isPublicRoute ? <Navigate to="/login" replace /> : null}

    <Helmet>
      <title>{capitalizeText(adminsMode)} Admin</title>
      {
        adminsMode === 'stilo' &&
        <link
          rel="icon"
          href={imagelogo}
          type="image/x-icon"
        />
      }
    </Helmet>
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <CssBaseline />
        <UnsavedChangesProvider>
          {layout === "dashboard" && pathname !== '/401' && pathname !== "/" && !pathname.includes('hyperdrive') && isLoggedIn('userData')?.isAuthenticated && (
            <>

              <Sidenav
                color={sidenavColor}
                brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
                brandName="Material Dashboard 2"
                routes={filterRoutes(
                  adminsMode === HYPERWOLF
                    ? hyperfwolfAdminRoutes
                    : adminsMode === HYPERDRIVE
                      ? hyperfDriveAdminRoutes
                      :
                      adminsMode === STILO ?
                        stiloRoutes
                        : routes
                )}
              />
              <Configurator />
            </>
          )}
          <Routes>
            <Route path="/login" element={<PublicRoutes><SignIn /></PublicRoutes>} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/distribution/reset-pincode" element={<ResetPincode />} />
            <Route path="/reset-hyperdrive-password" element={<ResetHyperdrivePassword />} />

            <Route path="/401" element={<PageNotFound />} />

            {adminsMode === HYPERWOLF ? getRoutes(hyperfwolfAdminRoutes) : adminsMode === HYPERDRIVE ? getRoutes(hyperfDriveAdminRoutes) : adminsMode === STILO ? getRoutes(stiloRoutes) : getRoutes(routes)}

            <Route path="/" element={
              (() => {
                const isAuthenticated = isLoggedIn()?.isAuthenticated;
                // If not authenticated, always go to login
                if (!isAuthenticated) {
                  return <Navigate to="/login" replace />;
                }
                // If authenticated and we have a valid screenToLoad (not '/' or '/login'), redirect there
                if (screenToLoad && screenToLoad !== '/' && screenToLoad !== '/login') {
                  return <Navigate to={screenToLoad} replace />;
                }
                // Fallback: if screenToLoad is not ready yet, stay on '/' (will redirect when ready)
                // Or if screenToLoad is '/', we're already here, so show a loading state or default
                return <Navigate to={screenToLoad || '/login'} replace />;
              })()
            } />

            <Route path="*" element={<Navigate to={isLoggedIn()?.isAuthenticated ? '/' : '/login'} replace />} />
          </Routes>
        </UnsavedChangesProvider>
        {
          isHyperdrive ?
            <NotificationCardHyperdrive style={{ paddingTop: '2px' }} /> :
            <NotificationCard style={{ paddingTop: '2px' }} />
        }


        <CustomModal />
        <CommonDrawer />
        <MDSnackbar
          {...{
            color,
            icon,
            content,
            open: isOpen,
            onClose: handleSnackbarClose,
            close: closeSnackbarMsg,
            bgWhite,
            anchorOrigin,
            autoHideDuration: persist ? null : 5000,
          }}
        />
      </LocalizationProvider>

    </ThemeProvider>
  </>
  );
}